import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Product, ProductService} from '@spartacus/core';
import {ConfiguratorRouterExtractorService} from '@spartacus/product-configurator/common';
import {
  Configurator,
  ConfiguratorCommonsService,
  ConfiguratorPriceSummaryComponent,
} from '@spartacus/product-configurator/rulebased';
import {combineLatest, Observable} from 'rxjs';
import {distinctUntilChanged, filter, map, tap} from 'rxjs/operators';
import {ICommercialModelConfig} from '../../models';
import {FeatureTogglesService, ProductUtilitiesService} from '../../services';
import {serviceProviderTokens, valueProviderTokens} from '../../token/tokens';
import * as ConfigUtils from '../../utils';
import {LICENSE_MATERIAL_ATTRIBUTE, TECHNICAL_GROUP_NAME, TRANSACTION_TYPE_ATTRIBUTE} from '../../utils';
import {DcpConfiguratorPriceService} from './dcp-configurator-price.service';
import PriceSummary = Configurator.PriceSummary;

@UntilDestroy()
@Component({
  selector: 'dcp-configurator-price-summary',
  templateUrl: './dcp-configurator-price-summary.component.html',
  styleUrls: ['./dcp-configurator-price-summary.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class DcpConfiguratorPriceSummaryComponent
  extends ConfiguratorPriceSummaryComponent
  implements OnInit, AfterViewChecked
{
  private cartDecoratorErrorHandlingService = inject(
    serviceProviderTokens.CART_DECORATOR_ERROR_HANDLING_SERVICE_PROVIDER,
  );
  private transactionTypeService = inject(serviceProviderTokens.TRANSACTION_TYPE_SERVICE_PROVIDER);
  private unleashToggles = inject(valueProviderTokens.FEATURE_TOGGLES);
  private dcpConfigService = inject(serviceProviderTokens.CONFIG_SERVICE_PROVIDER);

  product$?: Observable<Product | undefined>;
  totalValues = this.configuratorPriceService.getInitialTotalValues();
  showPricePerLicense$?: Observable<boolean>;
  showTaxDiscountsCalculationCheckoutMessage?: boolean;
  showSubtotalsPriceCalculationBreakdown$?: Observable<boolean>;
  showPerYearPriceCalculationBreakdown: boolean = false;
  showPriceDiscounts?: boolean;
  showPartnerDiscounts?: boolean;
  totalPartnerDiscountValue?: string | null;
  hasError$: Observable<boolean> = this.cartDecoratorErrorHandlingService.getErrorState$().pipe(map((e) => e.hasError));
  recurringDuration?: number | null;
  showGradientTop: boolean = false;
  showGradientBottom: boolean = false;
  priceWrapper = viewChild<ElementRef>('pricePerLicenseWrapper');

  constructor(
    public override configuratorCommonsService: ConfiguratorCommonsService,
    public override configRouterExtractorService: ConfiguratorRouterExtractorService,
    protected productService: ProductService,
    protected featureTogglesService: FeatureTogglesService,
    public configuratorPriceService: DcpConfiguratorPriceService,
    private productUtilitiesService: ProductUtilitiesService,
    private cdr: ChangeDetectorRef,
  ) {
    super(configuratorCommonsService, configRouterExtractorService);
  }

  ngOnInit(): void {
    this.showPartnerDiscounts = false;
    this.loadConfigurationData();
    this.getFeatureToggle();

    // check if gradient is needed each time items are added or removed
    this.configuration$.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe(() => {
      this.checkGradient();
    });
  }

  // check if gradient is needed from earlier configuration
  ngAfterViewChecked(): void {
    this.checkGradient();
  }

  private getFeatureToggle() {
    this.showPricePerLicense$ = this.featureTogglesService.isEnabled(this.unleashToggles.cpsPricePerLicense);
    this.showSubtotalsPriceCalculationBreakdown$ = this.featureTogglesService.isEnabled(
      this.unleashToggles.subtotalGroupPriceCalculationBreakdown,
    );
  }

  private loadConfigurationData() {
    this.configuration$
      .pipe(
        tap((configuration) => {
          this.transactionTypeService
            .getTransactionTypeConfiguration(
              ConfigUtils.getSelectedGroupAttributeValue(
                configuration,
                TECHNICAL_GROUP_NAME,
                TRANSACTION_TYPE_ATTRIBUTE,
              ),
            )
            .pipe(untilDestroyed(this))
            .subscribe((ttConfig) => {
              this.showTaxDiscountsCalculationCheckoutMessage = ttConfig?.showMessageTaxAndDiscount;
            });
          this.product$ = this.productService.get(configuration.productCode);
          if (configuration?.priceSummary?.priceSummaryDCP) {
            this.configuratorPriceService.writePriceTooltipData(configuration);
          }
          this.product$
            .pipe(
              filter((p): p is Product => !!p),
              untilDestroyed(this),
            )
            .subscribe((product) => this.getShowRecurringTotal(product, configuration));

          this.setPartnerDiscounts(configuration);
        }),
        map((configuration) => configuration.priceSummary),
        filter(Boolean),
        untilDestroyed(this),
      )
      .subscribe((priceSummary: PriceSummary) => {
        this.setTotalValues(priceSummary);
      });
  }

  private setTotalValues(priceSummary: PriceSummary) {
    if (priceSummary?.priceSummaryDCP) {
      this.totalValues = this.configuratorPriceService.getTotalValues(priceSummary.priceSummaryDCP);
    } else if (priceSummary.pricingError) {
      this.totalValues = this.configuratorPriceService.getInitialTotalValues();
    }
  }

  setPartnerDiscounts(configuration: Configurator.Configuration): void {
    const subtotalGroup = this.configuratorPriceService.getDcpSubtotalGroup(configuration, 'all');

    if (
      subtotalGroup?.discounts &&
      this.configuratorPriceService.isShowPartnerDiscounts(subtotalGroup) &&
      !configuration.priceSummary?.pricingError
    ) {
      this.totalPartnerDiscountValue = subtotalGroup.discounts[0].formattedValues?.totalDiscount;
      this.showPartnerDiscounts = true;
    } else {
      this.showPartnerDiscounts = false;
      this.totalPartnerDiscountValue = null;
    }
  }

  private getShowRecurringTotal(product: Product, configuration: Configurator.Configuration): void {
    const licenceMaterial =
      ConfigUtils.getSelectedGroupAttributeValue(
        configuration,
        TECHNICAL_GROUP_NAME,
        LICENSE_MATERIAL_ATTRIBUTE,
      )?.replace(/^0+/, '') || '';
    const currentBundleTemplate = this.productUtilitiesService.fetchBundleTemplateByAlternativeId(
      product,
      licenceMaterial,
    );
    combineLatest<[boolean, ICommercialModelConfig]>([
      this.featureTogglesService.isEnabled(this.unleashToggles.cpsShowRecurringTotal),
      this.dcpConfigService.getCommercialModelConfig(
        product,
        ConfigUtils.getSelectedGroupAttributeValue(configuration, TECHNICAL_GROUP_NAME, TRANSACTION_TYPE_ATTRIBUTE),
        currentBundleTemplate,
      ),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([enabled, commercialModelConfig]) => {
        if (commercialModelConfig?.calculateAutoLepDiscount) {
          this.showPriceDiscounts = true;
          this.cdr.detectChanges();
        }
        if (commercialModelConfig?.showPerYearPriceCalculationBreakdown) {
          this.showPerYearPriceCalculationBreakdown = true;
          this.cdr.detectChanges();
        }
        if (enabled && commercialModelConfig?.showRecurringTotal) {
          this.recurringDuration = this.configuratorPriceService.getDuration(configuration);
          this.cdr.detectChanges();
        }
      });
  }

  checkGradient(): void {
    const element = this.priceWrapper()?.nativeElement;
    if (element) {
      const {scrollTop, scrollHeight, clientHeight} = element;
      if (scrollTop !== undefined && scrollHeight !== undefined && clientHeight !== undefined) {
        // show gradient if not scrolled to top or bottom
        this.showGradientTop = scrollTop > 0;
        this.showGradientBottom = scrollHeight - scrollTop > clientHeight;

        // Add or remove the class based on the scroll height
        if (scrollHeight > clientHeight) {
          element.classList.add('scrollbar-shown');
        } else {
          element.classList.remove('scrollbar-shown');
        }
      } else {
        this.showGradientTop = false;
        this.showGradientBottom = false;
      }
    } else {
      this.showGradientTop = false;
      this.showGradientBottom = false;
    }
  }
}
